import styled, { keyframes } from 'styled-components/macro'
import {
  blueGradientDownward,
  commonModalContentStyle,
  darkGlassCard,
  gradientModalStyle,
  gradientModalHeaderStyle,
  gradientModalScrollbarStyle,
  squircle,
} from 'src/global/helpers'

// Closes back into the sidebar card
export const Modal = styled.div`
  ${props =>
    gradientModalStyle(
      blueGradientDownward,
      props.theme.blue,
      'translateY(-50%) translate(-505px, calc(605px - 50vh)) scale(0.01)',
    )}
  width: 580px;
`

export const Header = styled.div`
  ${gradientModalHeaderStyle}
`

export const ModalContent = styled.div`
  ${commonModalContentStyle}
  ${gradientModalScrollbarStyle}
  padding: 0 24px 24px;

  @media (max-width: 768px) {
    padding: 0 16px 16px;
  }
`

// The original ("Înainte", left of the divider) covers the retouched photo ("După", right of it), then gives
// way to it from the right, holds, and comes back, on a loop. The divider rides the edge; while holding it
// waits 12px outside the photo (line and shadow), where the frame hides it, and the clip edge travels the same
// 12px further so the two stay together
const uncoverAfter = keyframes`
  0%,
  12% {
    clip-path: inset(0 -12px 0 0);
  }
  42%,
  62% {
    clip-path: inset(0 calc(100% + 12px) 0 0);
  }
  92%,
  100% {
    clip-path: inset(0 -12px 0 0);
  }
`

const moveDivider = keyframes`
  0%,
  12% {
    left: calc(100% + 12px);
  }
  42%,
  62% {
    left: -12px;
  }
  92%,
  100% {
    left: calc(100% + 12px);
  }
`

export const Feature = styled.div<{ $accent: string; $accentDark: string }>`
  ${darkGlassCard}
  padding: 16px 24px 24px;
  margin-bottom: 16px;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }

  .feature-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;
  }

  /* The feature's own colour, on a disc with a glow in the same colour, like the subscription perks */
  .icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    background: linear-gradient(145deg, ${props => props.$accent}, ${props => props.$accentDark});
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.35),
      0 6px 14px ${props => props.$accentDark}66;
    font-size: 18px;
    color: #ffffff;
  }

  .feature-title {
    flex: 1;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.25;
    color: #ffffff;
  }

  .feature-age {
    flex-shrink: 0;
    padding: 2px 8px;
    ${squircle(20, 20)}
    background: rgba(255, 255, 255, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.35);
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
  }

  .feature-description {
    margin: 0 0 16px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.88);
  }

  .go-to {
    padding: 0;
    background: none;
    border: 0;
    font: inherit;
    font-weight: 700;
    color: #ffffff;
    white-space: nowrap;
    cursor: pointer;
  }

  /* Only the words are underlined, not the gap before the arrow */
  .go-to-label {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .go-to svg {
    margin-left: 8px;
    font-size: 14px;
  }

  .before-after {
    position: relative;
    overflow: hidden;
    ${squircle(24)}
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  }

  /* height: auto keeps the width and height attributes as a ratio: the box is reserved before the photo loads */
  .before-after .layer img {
    display: block;
    width: 100%;
    height: auto;
  }

  .before-layer {
    position: absolute;
    inset: 0;
    animation: ${uncoverAfter} 6s ease-in-out infinite;
  }

  .divider {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    margin-left: -1px;
    background: #ffffff;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.45);
    animation: ${moveDivider} 6s ease-in-out infinite;
  }

  /* Each label belongs to its photo, so it is only visible while that photo covers its corner */
  .label {
    position: absolute;
    top: 12px;
    padding: 2px 8px;
    ${squircle(20, 20)}
    background: rgba(0, 0, 0, 0.45);
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
  }

  .before-label {
    left: 12px;
  }

  .after-label {
    right: 12px;
  }

  /* The modal stays mounted while closed: the wipe stops with it */
  .closed & .before-layer,
  .closed & .divider {
    animation-play-state: paused;
  }

  /* Without motion: half and half, still */
  @media (prefers-reduced-motion: reduce) {
    .before-layer {
      animation: none;
      clip-path: inset(0 50% 0 0);
    }

    .divider {
      animation: none;
      left: 50%;
    }
  }

  .learn-more {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 16px;
    font-family: inherit;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.35);
    ${squircle(40)}
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .learn-more:hover {
    background: rgba(255, 255, 255, 0.35);
  }

  /* Phones: tighter padding, and the age goes under the title, in line with it */
  @media (max-width: 768px) {
    padding: 16px;

    .feature-header {
      flex-wrap: wrap;
      row-gap: 8px;
    }

    .feature-title {
      flex-basis: calc(100% - 56px);
    }

    .feature-age {
      margin-left: 56px;
    }
  }

  .screenshot {
    display: block;
    width: 100%;
    height: auto;
    ${squircle(24)}
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  }
`
